import { type ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from 'sonner';

import { isRunningInNativeApp, native } from '@muzly/shared';

import { SUPPORT_EMAIL, contactMailUrl } from './contactMail';
import { APP_VERSION } from '../../core';

const MAIL_LINK = 'font-semibold text-bk-900 underline underline-offset-2';

/** 복사에 성공했을 때만 true — 안내 문구가 "복사했어요"라고 말해도 되는지 가른다. */
const copyText = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // API 부재·거절(WKWebView 는 브리지 왕복 뒤 사용자 제스처가 끝났다고 볼 수 있다).
    return false;
  }
};

/**
 * 문의 메일을 여는 동작 하나 — 문의 화면의 버튼과 문서·FAQ 본문의 `support@muzly.io` 가
 * 같이 쓴다.
 *
 * 브라우저는 `mailto:` 링크다. 앱 안에서는 링크가 아니라 브리지(`openExternalUrl`)로 연다 —
 * WebView 가 `mailto:` 이동을 OS 에 넘기는 경로는 실패를 삼켜서, 메일 앱이 없으면 눌러도
 * 아무 반응이 없었다. 브리지가 실패하면 주소를 복사하고 토스트로 알린다.
 */
export const useContactMail = () => {
  const { t } = useTranslation();

  // 연타 방어 — 브리지 왕복 동안 다시 누르면 메일 작성 화면·토스트가 겹쳐 뜬다.
  const mailPending = useRef(false);

  const openMailInApp = async (): Promise<void> => {
    if (mailPending.current) {
      return;
    }
    mailPending.current = true;
    try {
      // 본문 보조 정보 — 못 받아도 메일은 연다. 기본 재시도(5초 × 3)를 기다리면 누른 뒤 한참
      // 아무 반응이 없다(고치려던 증상과 같은 모양), 그래서 짧게 한 번만 묻는다.
      const quick = { timeout: 1000, retries: 1 };
      const [app, device] = await Promise.all([
        native.invoke('getAppVersion', undefined, quick).catch(() => undefined),
        native.invoke('getDeviceInfo', undefined, quick).catch(() => undefined),
      ]);
      try {
        await native.invoke('openExternalUrl', {
          url: contactMailUrl(t, { web: APP_VERSION, app, device }),
        });
      } catch {
        const copied = await copyText(SUPPORT_EMAIL);
        toast(
          t(
            copied
              ? 'web.more.contactMail.failedCopied'
              : 'web.more.contactMail.failed',
            { email: SUPPORT_EMAIL },
          ),
        );
      }
    } finally {
      mailPending.current = false;
    }
  };

  /** 메일을 여는 요소 — 앱은 버튼(브리지), 브라우저는 `mailto:` 링크. */
  const mailAction = (className: string, children: ReactNode) =>
    isRunningInNativeApp() ? (
      <button
        type="button"
        onClick={() => void openMailInApp()}
        className={className}
      >
        {children}
      </button>
    ) : (
      <a href={contactMailUrl(t, { web: APP_VERSION })} className={className}>
        {children}
      </a>
    );

  /** 글 안의 `support@muzly.io` 를 같은 동작의 링크로 바꾼다. */
  const withMailLinks = (text: string): ReactNode[] =>
    text
      .split(SUPPORT_EMAIL)
      .flatMap((part, i) =>
        i === 0
          ? [part]
          : [<span key={i}>{mailAction(MAIL_LINK, SUPPORT_EMAIL)}</span>, part],
      );

  return { mailAction, withMailLinks };
};
